import { useState } from "react"
import { Plus, Pencil, Trash2, Crown } from "lucide-react"
import {
  useGetSubscriptionPlansQuery,
  useCreateSubscriptionPlanMutation,
  useUpdateSubscriptionPlanMutation,
  useDeleteSubscriptionPlanMutation,
  useGetBusinessSubscriptionsQuery,
  useAssignBusinessSubscriptionMutation,
  type SubscriptionPlan,
  type AdminBusinessSubscription,
} from "../features/admin/adminApi"

// ── helpers ────────────────────────────────────────────────────────────────

const INTERVAL_LABELS: Record<string, string> = {
  monthly: "/ month",
  yearly: "/ year",
  lifetime: "Forever",
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    active: "bg-green-100 text-green-700",
    inactive: "bg-gray-100 text-gray-500",
    cancelled: "bg-red-100 text-red-600",
    expired: "bg-yellow-100 text-yellow-700",
    trial: "bg-blue-100 text-blue-700",
  }
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium capitalize ${map[status] ?? "bg-gray-100 text-gray-500"}`}>
      {status}
    </span>
  )
}

// ── Plan form modal ────────────────────────────────────────────────────────

const emptyForm = (): Partial<SubscriptionPlan> => ({
  name: "", description: "", price: 0, currency: "NGN",
  interval: "monthly", intervalCount: 1,
  maxProducts: undefined, maxServices: undefined,
  isFeatured: false, status: "active",
})

function PlanModal({
  initial, onSave, onClose, saving,
}: {
  initial: Partial<SubscriptionPlan>
  onSave: (d: Partial<SubscriptionPlan>) => void
  onClose: () => void
  saving: boolean
}) {
  const [form, setForm] = useState<Partial<SubscriptionPlan>>(initial)
  const set = (k: keyof SubscriptionPlan, v: any) => setForm((f) => ({ ...f, [k]: v }))

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6 space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">
          {initial.id ? "Edit Plan" : "Create Subscription Plan"}
        </h2>

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="block text-xs text-gray-500 mb-1">Plan Name *</label>
            <input
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={form.name ?? ""}
              onChange={(e) => set("name", e.target.value)}
              placeholder="e.g. Free, Starter, Pro"
            />
          </div>

          <div className="col-span-2">
            <label className="block text-xs text-gray-500 mb-1">Description</label>
            <textarea
              rows={2}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={form.description ?? ""}
              onChange={(e) => set("description", e.target.value)}
              placeholder="Brief description of the plan"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">Price</label>
            <input
              type="number" min={0}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={form.price ?? 0}
              onChange={(e) => set("price", parseFloat(e.target.value) || 0)}
            />
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">Currency</label>
            <select
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={form.currency ?? "NGN"}
              onChange={(e) => set("currency", e.target.value)}
            >
              {["NGN", "USD", "GBP", "EUR"].map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">Billing Interval</label>
            <select
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={form.interval ?? "monthly"}
              onChange={(e) => set("interval", e.target.value as SubscriptionPlan["interval"])}
            >
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
              <option value="lifetime">Lifetime (Free)</option>
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">Interval Count</label>
            <input
              type="number" min={1}
              disabled={form.interval === "lifetime"}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              value={form.intervalCount ?? 1}
              onChange={(e) => set("intervalCount", parseInt(e.target.value) || 1)}
            />
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">Max Products</label>
            <input
              type="number" min={0} placeholder="Unlimited"
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={form.maxProducts ?? ""}
              onChange={(e) => set("maxProducts", e.target.value ? parseInt(e.target.value) : undefined)}
            />
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">Max Services</label>
            <input
              type="number" min={0} placeholder="Unlimited"
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={form.maxServices ?? ""}
              onChange={(e) => set("maxServices", e.target.value ? parseInt(e.target.value) : undefined)}
            />
          </div>

          <div className="col-span-2 flex items-center gap-2">
            <input
              id="is-featured"
              type="checkbox"
              checked={!!form.isFeatured}
              onChange={(e) => set("isFeatured", e.target.checked)}
              className="rounded"
            />
            <label htmlFor="is-featured" className="text-sm text-gray-700">Mark as Featured</label>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">Status</label>
            <select
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={form.status ?? "active"}
              onChange={(e) => set("status", e.target.value as SubscriptionPlan["status"])}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border rounded-lg text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(form)}
            disabled={saving || !form.name}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save Plan"}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Assign Plan modal ──────────────────────────────────────────────────────

function AssignModal({
  sub, plans, onSave, onClose, saving,
}: {
  sub: AdminBusinessSubscription
  plans: SubscriptionPlan[]
  onSave: (planId: string) => void
  onClose: () => void
  saving: boolean
}) {
  const [planId, setPlanId] = useState(sub.plan?.id ?? "")

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6 space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">Assign Plan</h2>
        <p className="text-sm text-gray-500">Business: <span className="font-medium text-gray-800">{sub.businessName ?? sub.businessUid}</span></p>

        <div>
          <label className="block text-xs text-gray-500 mb-1">Select Plan</label>
          <select
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={planId}
            onChange={(e) => setPlanId(e.target.value)}
          >
            <option value="">— choose —</option>
            {plans.filter(p => p.status === "active").map(p => (
              <option key={p.id} value={p.id}>{p.name} — {p.price === 0 ? "Free" : `${p.currency} ${p.price.toLocaleString()} ${INTERVAL_LABELS[p.interval] ?? p.interval}`}</option>
            ))}
          </select>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border rounded-lg text-gray-600 hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={() => onSave(planId)}
            disabled={saving || !planId}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Assign"}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Plans tab ──────────────────────────────────────────────────────────────

function PlansTab() {
  const { data: plans = [], isLoading } = useGetSubscriptionPlansQuery()
  const [createPlan, { isLoading: creating }] = useCreateSubscriptionPlanMutation()
  const [updatePlan, { isLoading: updating }] = useUpdateSubscriptionPlanMutation()
  const [deletePlan] = useDeleteSubscriptionPlanMutation()

  const [modal, setModal] = useState<{ open: boolean; plan: Partial<SubscriptionPlan> }>({ open: false, plan: emptyForm() })

  const handleSave = async (form: Partial<SubscriptionPlan>) => {
    if (form.id) await updatePlan({ id: form.id, ...form })
    else await createPlan(form)
    setModal((m) => ({ ...m, open: false }))
  }

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this plan? Plans with active subscribers cannot be deleted.")) return
    await deletePlan(id)
  }

  if (isLoading) return <div className="text-sm text-gray-400 text-center py-16">Loading…</div>

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-500">{plans.length} plan{plans.length !== 1 ? "s" : ""}</span>
        <button
          onClick={() => setModal({ open: true, plan: emptyForm() })}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
        >
          <Plus size={16} /> New Plan
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {plans.map((plan) => (
          <div key={plan.id} className="border rounded-xl p-5 bg-white relative space-y-3 shadow-sm hover:shadow-md transition-shadow">
            {plan.isFeatured && (
              <span className="absolute top-3 right-3 flex items-center gap-1 text-xs bg-yellow-50 text-yellow-700 border border-yellow-200 px-2 py-0.5 rounded-full font-medium">
                <Crown size={11} /> Featured
              </span>
            )}
            <div>
              <h3 className="font-semibold text-gray-900">{plan.name}</h3>
              {plan.description && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{plan.description}</p>}
            </div>
            <div className="flex items-end gap-1">
              <span className="text-2xl font-bold text-gray-900">
                {plan.price === 0 ? "Free" : `${plan.currency} ${plan.price.toLocaleString()}`}
              </span>
              {plan.price > 0 && (
                <span className="text-sm text-gray-400 pb-0.5">{INTERVAL_LABELS[plan.interval] ?? plan.interval}</span>
              )}
            </div>
            <div className="text-xs text-gray-500 space-y-0.5">
              {plan.maxProducts != null && <div>Max {plan.maxProducts} products</div>}
              {plan.maxServices != null && <div>Max {plan.maxServices} services</div>}
              {plan.maxProducts == null && plan.maxServices == null && <div>Unlimited products &amp; services</div>}
            </div>
            <div className="flex items-center justify-between pt-1">
              <StatusBadge status={plan.status} />
              <div className="flex gap-3">
                <button onClick={() => setModal({ open: true, plan: { ...plan } })} className="text-blue-600 hover:text-blue-800">
                  <Pencil size={15} />
                </button>
                <button onClick={() => handleDelete(plan.id)} className="text-red-500 hover:text-red-700">
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          </div>
        ))}

        {plans.length === 0 && (
          <div className="col-span-3 text-center text-gray-400 py-16">
            No subscription plans yet. Create your first plan.
          </div>
        )}
      </div>

      {modal.open && (
        <PlanModal
          initial={modal.plan}
          onSave={handleSave}
          onClose={() => setModal((m) => ({ ...m, open: false }))}
          saving={creating || updating}
        />
      )}
    </div>
  )
}

// ── Subscriptions tab ──────────────────────────────────────────────────────

function SubscriptionsTab() {
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState("")
  const { data, isLoading } = useGetBusinessSubscriptionsQuery({ page, status: statusFilter || undefined })
  const { data: plans = [] } = useGetSubscriptionPlansQuery()
  const [assignSub, { isLoading: assigning }] = useAssignBusinessSubscriptionMutation()
  const [assignModal, setAssignModal] = useState<AdminBusinessSubscription | null>(null)

  const subscriptions = data?.subscriptions ?? []

  const handleAssign = async (planId: string) => {
    if (!assignModal) return
    await assignSub({ businessUid: assignModal.businessUid, planId })
    setAssignModal(null)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <select
          className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}
        >
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="cancelled">Cancelled</option>
          <option value="expired">Expired</option>
          <option value="trial">Trial</option>
        </select>
        <span className="text-sm text-gray-400">{data?.total ?? 0} total</span>
      </div>

      {isLoading ? (
        <div className="text-sm text-gray-400 text-center py-16">Loading…</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b text-gray-500 text-left">
              <tr>
                <th className="px-4 py-3 font-medium">Business</th>
                <th className="px-4 py-3 font-medium">Plan</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Start</th>
                <th className="px-4 py-3 font-medium">Expires</th>
                <th className="px-4 py-3 font-medium">Auto-Renew</th>
                <th className="px-4 py-3 font-medium">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {subscriptions.map((s) => (
                <tr key={s.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{s.businessName ?? s.businessUid}</td>
                  <td className="px-4 py-3 text-gray-700">{s.plan?.name ?? "—"}</td>
                  <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
                  <td className="px-4 py-3 text-gray-500">{s.startDate ? new Date(s.startDate).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-3 text-gray-500">{s.endDate ? new Date(s.endDate).toLocaleDateString() : "Lifetime"}</td>
                  <td className="px-4 py-3 text-gray-500">{s.autoRenew ? "Yes" : "No"}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => setAssignModal(s)}
                      className="text-xs text-blue-600 hover:underline font-medium"
                    >
                      Change Plan
                    </button>
                  </td>
                </tr>
              ))}
              {subscriptions.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-gray-400">No subscriptions found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex justify-end gap-2">
        <button
          disabled={page <= 1}
          onClick={() => setPage((p) => p - 1)}
          className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-40 text-gray-600"
        >
          Prev
        </button>
        <span className="px-3 py-1.5 text-sm text-gray-400">Page {page}</span>
        <button
          disabled={subscriptions.length < 20}
          onClick={() => setPage((p) => p + 1)}
          className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-40 text-gray-600"
        >
          Next
        </button>
      </div>

      {assignModal && (
        <AssignModal
          sub={assignModal}
          plans={plans}
          onSave={handleAssign}
          onClose={() => setAssignModal(null)}
          saving={assigning}
        />
      )}
    </div>
  )
}

// ── Page ───────────────────────────────────────────────────────────────────

type Tab = "plans" | "subscriptions"

export default function SubscriptionPlansPage() {
  const [tab, setTab] = useState<Tab>("plans")

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Subscription Plans</h1>
        <p className="text-sm text-gray-500 mt-1">Manage subscription plans and business subscriptions</p>
      </div>

      <div className="flex gap-1 border-b">
        {(["plans", "subscriptions"] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px capitalize transition-colors ${
              tab === t
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-800"
            }`}
          >
            {t === "plans" ? "Subscription Plans" : "Business Subscriptions"}
          </button>
        ))}
      </div>

      {tab === "plans" && <PlansTab />}
      {tab === "subscriptions" && <SubscriptionsTab />}
    </div>
  )
}
